import React, {useState, useEffect} from 'react';
import {Icon, PriorityIcon, Button, Dialog} from '@priority-software/priority-style-react';
import {Header} from './Header';
import {Content} from './Content';
import {Footer} from './Footer';
import styles from '../styles/settingsPanel.module.scss';
import {Record} from '../../../../../../stores/Form/Record';
import {SettingsPanelCol, MonitoredActions} from '../../Common/consts';
import {createDataTestId} from '../../../../../../utils/dataTestId';

type SettingsPanelProps = {
    cols?: SettingsPanelCol[];
    records: Record[];
    defaultCols: string[];
    enabledCols: string[];
    applyChanges: (cols: SettingsPanelCol[]) => Promise<void>;
    monitorSearchBlock: (MonitoredActions) => void;
    headerHasTabs?: boolean;
};

export const SettingsPanel = ({
    records,
    applyChanges,
    defaultCols,
    enabledCols,
    monitorSearchBlock,
    headerHasTabs = false
}: SettingsPanelProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [cols, setCols] = useState(null);

    useEffect(() => {
        setInitialEnabledCols();
    }, []);

    const setInitialEnabledCols = () => {
        const settingCols = records[0]?.parentForm?.visibleColumns.map(col => {
            return {
                title: col.Title,
                name: col.Name,
                isDefault: defaultCols?.includes(col.Name),
                isChecked: enabledCols?.includes(col.Name)
            };
        });
        setCols(settingCols);
    };

    const onReset = () => {
        const resetCols = cols.map(col => {
            return {...col, isChecked: false};
        });
        setCols(resetCols);
        if (!isDirty) {
            setIsDirty(true);
        }
    };

    const onCancel = () => {
        if (isDirty) {
            setInitialEnabledCols();
            setIsDirty(false);
        }
        setIsOpen(false);
    };

    const handleCloseAndApply = () => {
        if (isDirty) {
            applyChanges(cols);
            setIsDirty(false);
        }
        setIsOpen(false);
    };

    const handleChange = () => {
        if (!isDirty) {
            setIsDirty(true);
        }
    };

    const toggleOpen = () => {
        if (!isOpen) {
            monitorSearchBlock(MonitoredActions.Settings);
        }
        setIsOpen(prev => !prev);
    };

    return (
        <>
            <Button
                icon={<Icon icon={PriorityIcon.settings} />}
                size="medium"
                skin={'secondary'}
                active={isOpen}
                componentClasses={styles.transparentBtn}
                onClick={toggleOpen}
                {...createDataTestId(`settings-button`, true)}
            />
            <Dialog
                isOpen={isOpen}
                closable={false}
                mask={false}
                maskClosable
                width={292}
                componentClasses={styles.settingsPanel}
                style={{marginTop: headerHasTabs ? '352px' : '290px'}}
                onClose={() => handleCloseAndApply()}
                content={
                    <div {...createDataTestId(`settings-content`, false)}>
                        <Header onReset={onReset} />
                        <Content cols={cols} handleChange={handleChange} />
                        <Footer
                            isDirty={isDirty}
                            onCancel={onCancel}
                            onApply={handleCloseAndApply}
                        />
                    </div>
                }
            />
        </>
    );
};
